import React from 'react';
import { Button, Box, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitAgeRangeAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { useSelector } from 'react-redux';

export const AgeRangeForm = props => {
  const { data, submitCallback, handleClose, allResorts, allSeasons } = props;
  const { module } = useSelector(state => state.app);

  const { loading, mutate } = useMutation(submitAgeRangeAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    age_from: Yup.number().min(0, 'Wartość musi być większa niż 0')
      .nullable(),
    age_to: Yup.number().min(0, 'Wartość musi być większa niż 0')
      .nullable(),
    cms_order: Yup.number().min(0, 'Wartość musi być większa niż 0')
      .nullable()
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    age_from: data ? data.age_from : null,
    age_to: data ? data.age_to : null,
    resorts: data ? data.resorts : [],
    seasons: data ? data.seasons : [],
    cms_name: data ? data.cms_name || '' : '',
    cms_order: data ? data.cms_order || 1 : 1
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano przedział wiekowy' : 'Dodano nowe przedział wiekowy', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego przedziału wiekowego');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto przedział wiekowy ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć przedziału wiekowego ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1} ml="5%" mr="5%">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa przedziału wiekowego"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Od lat</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="age_from"
              id="age_from"
              type="number"
              placeholder="Od lat"
              value={formik.values.age_from}
              onChange={formik.handleChange}
              error={formik.touched.age_from && Boolean(formik.errors.age_from)}
              helperText={formik.touched.age_from && formik.errors.age_from}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Do</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="age_to"
              id="age_to"
              type="number"
              placeholder="Do lat"
              value={formik.values.age_to}
              onChange={formik.handleChange}
              error={formik.touched.age_to && Boolean(formik.errors.age_to)}
              helperText={formik.touched.age_to && formik.errors.age_to}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          { module == 'obozy' || module == 'polkolonie' ? <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ośrodki</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="resorts"
              name="resorts"
              options={allResorts}
              value={formik.values.resorts}
              onChange={formik.handleChange} />
          </Stack> : null}
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Sezony</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="seasons"
              name="seasons"
              options={allSeasons}
              value={formik.values.seasons}
              onChange={formik.handleChange} />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa CMS</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="cms_name"
              id="cms_name"
              type="text"
              placeholder="nazwa przedziału wiekowego CMS"
              value={formik.values.cms_name}
              onChange={formik.handleChange}
              error={formik.touched.cms_name && Boolean(formik.errors.cms_name)}
              helperText={formik.touched.cms_name && formik.errors.cms_name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Kolejność CMS</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="cms_order"
              id="cms_order"
              type="text"
              placeholder="kolejność wyświetlania przedziału wiekowego CMS"
              value={formik.values.cms_order}
              onChange={formik.handleChange}
              error={formik.touched.cms_order && Boolean(formik.errors.cms_order)}
              helperText={formik.touched.cms_order && formik.errors.cms_order}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
            disabled={loading}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć Zakwaterowanie?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć te przedział wiekowy?
      </ConfirmDialog>
    </>

  );
};
